import { useQueryClient } from '@tanstack/react-query';

import { ANALYTICS_EVENTS } from '../../../constants/analytics';
import { useWorkspaceId } from '../../../contexts/WorkspaceContext';
import { useCreateArtifact } from '../../../hooks/data/useArtifacts';
import { useAppToast } from '../../../hooks/ui/useAppToast';
import { refreshFolderLists } from '../../../lib/folderListRefresh';
import { safeAnalytics } from '../../../utils/analytics';
import { destinationProblem } from '../create/destination/createDestination';
import type { LinkFieldValues, LinkTitleSource } from './linkPrefill';

interface UseCreateLinkOptions {
  readonly surface: string;
  /** The parent refused the create; given, it replaces the error toast for those failures. */
  readonly onDestinationProblem?: (problem: 'gone' | 'forbidden') => void;
}

export interface CreateLinkInput {
  readonly parent: {
    readonly parentEntityType: 'workspace' | 'project';
    readonly parentEntityId: string;
  };
  readonly values: LinkFieldValues;
  readonly titleSource: LinkTitleSource;
}

/**
 * Create a link artifact (docs/planning/link-artifacts.md): content is exactly
 * `{ url }` with the URL already normalized, and `metadata.favicon` is sent
 * only when the site icon was chosen. The user stays where they are; the
 * folder lists refresh so the new link shows in place. It never opens the link.
 */
export function useCreateLink({ surface, onDestinationProblem }: UseCreateLinkOptions) {
  const createArtifact = useCreateArtifact();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const toast = useAppToast();

  const create = async ({ parent, values, titleSource }: CreateLinkInput): Promise<boolean> => {
    try {
      await createArtifact.mutateAsync({
        artifactType: 'link',
        associatedParent: parent,
        title: values.title,
        content: { url: values.url },
        ...(values.favicon ? { metadata: { favicon: values.favicon } } : {}),
      });
    } catch (error) {
      const problem = onDestinationProblem ? destinationProblem(error) : null;
      if (problem && onDestinationProblem) onDestinationProblem(problem);
      else {
        toast({
          title: 'Could not add the link',
          description: error instanceof Error ? error.message : undefined,
          status: 'error',
        });
      }
      return false;
    }
    // Never the URL, its host or the title: they are user content.
    safeAnalytics.track(ANALYTICS_EVENTS.LINK_CREATED, {
      surface,
      title_source: titleSource,
      icon: values.favicon ? 'site' : 'bower',
    });
    refreshFolderLists(queryClient, { queryKey: ['workspace-items', { workspaceId }] });
    toast({ title: 'Link added', status: 'success' });
    return true;
  };

  return { create, isPending: createArtifact.isPending };
}
